import { Dialog, Heading } from '@madpaws/design-system';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import { usePathname } from 'next/navigation';
import React, { useCallback, useEffect, useMemo } from 'react';

import { ImageWithFallback } from '~/common/components/ImageWithFallback/ImageWithFallback';
import { isAppOpenedInNativeAppBrowser } from '~/common/utils/app';
import { useDeviceType } from '~/hooks/useDeviceType';

import styles from './FirstBookingPromoDialog.module.css';
import { useAuthenticationContext } from '../AuthenticationProvider/useAuthenticationContext';
import {
  NEW_CUSTOMER_COUPON_JSON_EPPO_ID,
  NEW_CUSTOMER_COUPON_JSON_EPPO_JSON_PARAMS,
} from '../Eppo/constants';
import { useEppoJSONAssignment, validateEppoJson } from '../Eppo/helpers';
import { trackEvent } from '../analytics/analytics';
import {
  PROMO_BANNER_CTA_CLICK_EVENT_NAME,
  PROMO_MODAL_CTA_CLICK_EVENT_NAME,
  PROMO_MODAL_DISMISSED_EVENT_NAME,
  PROMO_MODAL_VIEWED_EVENT_NAME,
} from '../analytics/constants';
import { DOM_CONTAINER_ID } from '../constants';

import type { EppoFirstBookingPromoDataType } from '../Eppo/types';
import type { ReactElement } from 'react';

const { publicRuntimeConfig } = getConfig();

export const FirstBookingPromoDialog = (): ReactElement | null => {
  const { isUserLoggedIn } = useAuthenticationContext();
  const deviceType = useDeviceType();
  const pathname = usePathname();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const isFromInappBrowser = useMemo(() => isAppOpenedInNativeAppBrowser(), []);

  const eppoData = useEppoJSONAssignment(
    NEW_CUSTOMER_COUPON_JSON_EPPO_ID
  ) as EppoFirstBookingPromoDataType;
  const validEppoData = validateEppoJson(eppoData, NEW_CUSTOMER_COUPON_JSON_EPPO_JSON_PARAMS);

  const PROMO_DIALOG_ID = eppoData.promo;
  const FIRST_BOOKING_PROMO_DIALOG_COOKIE_NAME = `${PROMO_DIALOG_ID}_promo_dialog_opened`;
  const eventParams = useMemo(
    () => ({
      device_type: deviceType,
      page: pathname,
      type: PROMO_DIALOG_ID,
    }),
    [deviceType, pathname, PROMO_DIALOG_ID]
  );

  const openDialog = useCallback((): void => {
    if (isFromInappBrowser) {
      return;
    }

    Cookies.set(FIRST_BOOKING_PROMO_DIALOG_COOKIE_NAME, 'true');
    setIsDialogOpen(true);
  }, [FIRST_BOOKING_PROMO_DIALOG_COOKIE_NAME, isFromInappBrowser]);

  useEffect(() => {
    if (validEppoData && !isUserLoggedIn && !Cookies.get(FIRST_BOOKING_PROMO_DIALOG_COOKIE_NAME)) {
      openDialog();
    }
  }, [isUserLoggedIn, openDialog, validEppoData, FIRST_BOOKING_PROMO_DIALOG_COOKIE_NAME]);

  const { openAuthDialog } = useAuthenticationContext();

  const closeDialog = (): void => {
    setIsDialogOpen(false);
    trackEvent(PROMO_MODAL_DISMISSED_EVENT_NAME, eventParams);
  };

  const onClickCta = (): void => {
    setIsDialogOpen(false);
    openAuthDialog({
      title: eppoData?.authModalTitle ?? 'Create a free account',
      successRedirectUrl: pathname,
      queryParam: PROMO_DIALOG_ID,
    });
    trackEvent(PROMO_MODAL_CTA_CLICK_EVENT_NAME, eventParams);
  };

  if (isUserLoggedIn || !validEppoData) {
    return null;
  }

  const onPressBannerCta = (): void => {
    openDialog();
    trackEvent(PROMO_BANNER_CTA_CLICK_EVENT_NAME, eventParams);
  };

  const handleAfterOpenDialog = (): void => {
    trackEvent(PROMO_MODAL_VIEWED_EVENT_NAME, eventParams);
  };

  const modalTitle = eppoData?.modalTitle ?? 'New Here?';
  const ctaLabel = eppoData?.btnText ?? 'Sign up & unlock';
  // eslint-disable-next-line
  const tncText = eppoData?.tncText ?? "T&C's apply";

  if (isFromInappBrowser) {
    return null;
  }

  return (
    <div>
      <div className={styles.promoBanner}>
        <p onClick={onPressBannerCta}>{eppoData?.bannerTitle}</p>
      </div>
      <Dialog
        cta={{ label: ctaLabel, onClick: onClickCta }}
        domContainerId={DOM_CONTAINER_ID}
        heading={modalTitle}
        isOpen={isDialogOpen}
        onAfterOpen={handleAfterOpenDialog}
        onRequestClose={closeDialog}
      >
        <div className={styles.root}>
          <ImageWithFallback
            alt="Picture of a pet"
            fallback={`${publicRuntimeConfig.staticPath}/images/promo/summer.png`}
            loading="lazy"
            webpSrc={eppoData?.image}
            width={200}
          />

          <Heading level={3}>{eppoData?.title}</Heading>
          <p>{eppoData?.description}</p>
          <a className={styles.root} href={eppoData?.tncLink} rel="noreferrer" target="_blank">
            {tncText}
          </a>
        </div>
      </Dialog>
    </div>
  );
};
